<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted, toRef } from 'vue';
import ImageLazy from './ImageLazy.vue';
import MockChatComposer from './MockChatComposer.vue';
import SeedeFrameLoading from '@/components/features/v3/SeedeFrameLoading.vue';
import { wgpSlideMeta, type WgpSlideMeta } from '@/components/mock-canvas/slides/wgp/slides.config';
import { i18n, t } from '@/i18n';
import { DECK_COPY_ATTRS } from '@/composables/useDeckEnglishOnly';

const locale = toRef(i18n.options, 'locale');
const isJapanese = computed(() => locale.value === 'ja');

/** 用户消息上挂载的"已上传文件"元数据。用于展示"用一份文档生成设计"的输入语义。 */
interface ChatAttachment {
  /** 文件名（含后缀） */
  name: string;
  /** 文件大小展示文本，例如 "1.8 KB" / "4.7 MB" */
  size?: string;
  /** 文件类型，决定 chip 上的图标颜色 */
  type?: 'doc' | 'pdf' | 'md' | 'image';
}

interface Props {
  variant?: 'dark' | 'light';
  title?: string;
  demoUserMessage?: string;
  demoAiMessage?: ChatMessage;
  /** 模拟用户在输入框中上传的文件。设置后会同时显示在 input 上方 pending chip 与 user 气泡内。 */
  demoUserAttachment?: ChatAttachment;
  /** deck slide 列表；不传时默认走 WGP 16:9 deck */
  deckSlides?: WgpSlideMeta[];
  /** 单个 frame 的渲染宽度（px）。需与 slide 内部 stage transform 缩放保持一致 */
  artboardWidth?: number;
  /** 单个 frame 的渲染高度（px） */
  artboardHeight?: number;
  /** 网格最大列数，默认 3 */
  deckMaxColumns?: number;
  /** 自由布局模式下整个画板的宽度（px）。只在 slide 自带 x/y 时生效 */
  deckWidth?: number;
  /** 自由布局模式下整个画板的高度（px） */
  deckHeight?: number;
  /**
   * 初始展示状态。
   * - 'complete'（默认，向后兼容）：直接渲染 demo user / ai message + artboard。
   * - 'empty'：仅渲染输入框（带 pending attachment chip），等待父组件用 typeText / sendDemoMessage
   *   / startArtboardLoading / showAiMessageAnimated / finishArtboardLoading 主动驱动动画。
   */
  initialState?: 'complete' | 'empty';
  /** demo 模式默认收起右侧 chat，把画布让给作品展示 */
  defaultSidebarOpen?: boolean;
  /** 播放/演示时跳过 AI 对话气泡（仅保留输入区 + 画板） */
  hideChatHistory?: boolean;
  /** landing mobile：允许单指纵向滑动滚页，不拦截 touch */
  allowPageScroll?: boolean;
  /** landing mobile：隐藏 Share 等 chrome，画布最大化 */
  compactChrome?: boolean;
  /** 页面滚动中暂停 demo 动效与打字计时，保留当前帧 */
  playbackPaused?: boolean;
  /** 底部 composer 是否根据内容自动增高；demo 展示可关闭以避免长 prompt 撑高输入区 */
  composerAutoResize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'dark',
  /** title 默认值留空字符串，模板里用 `props.title || $t('landing_home.mock.canvas.untitled_design')` 兜底。
   *  这样切换 locale 时未传 title 的父级也能跟着变；显式传 title 的父级（如 OneInputManyOutputsV3）
   *  传 i18n 翻译后的字符串即可。 */
  title: '',
  initialState: 'complete',
  defaultSidebarOpen: undefined,
  hideChatHistory: false,
  allowPageScroll: false,
  compactChrome: false,
  playbackPaused: false,
  composerAutoResize: true,
});

const deckSlides = computed<WgpSlideMeta[]>(() => props.deckSlides ?? wgpSlideMeta);

// Container ref
const containerRef = ref<HTMLDivElement | null>(null);

// State
const zoom = ref(1);
const offset = ref({ x: 0, y: 0 });
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });

const resolveSidebarOpen = (explicit?: boolean) => explicit ?? props.initialState === 'complete';

// Sidebar state — demo（empty）默认收起，让画布展示作品
const isSidebarOpen = ref(resolveSidebarOpen(props.defaultSidebarOpen));

watch(
  () => props.defaultSidebarOpen,
  (open) => {
    if (typeof open === 'boolean') {
      isSidebarOpen.value = open;
    }
  }
);

// Selection state - 'artboard' 表示 frame 本身被选中，其它字符串为 slide 内元素 id
type SelectableElement = string | null;
const selectedElement = ref<SelectableElement>(null);

const isArtboardSelected = computed(() => selectedElement.value === 'artboard');

const handleCanvasClick = () => {
  selectedElement.value = null;
};

// Share panel state
const isSharePanelOpen = ref(false);
const shareUrl = 'https://veeso.ai';
const shareText = computed(() => {
  void locale.value;
  return t('landing_home.mock.canvas.share_text');
});

const toggleSharePanel = () => {
  isSharePanelOpen.value = !isSharePanelOpen.value;
};

const copyShareLink = async () => {
  const fullText = `${shareText.value} ${shareUrl}`;
  try {
    await navigator.clipboard.writeText(fullText);
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = fullText;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

// Tool state
type ToolType = 'select' | 'deep-select' | 'hand' | 'artboard';
const activeTool = ref<ToolType>('select');

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const setSidebarOpen = (open: boolean) => {
  isSidebarOpen.value = open;
};

const _setTool = (tool: ToolType) => {
  activeTool.value = tool;
};

// Chat messages
interface ChatMessage {
  id: number;
  role: 'user' | 'ai';
  content: string;
  // User message specific fields
  attachment?: ChatAttachment;
  // AI message specific fields
  steps?: { label: string; done: boolean }[];
  sections?: {
    id: string;
    label: string;
    content?: string;
    value?: string;
    color?: string;
    icon?: string;
  }[];
  isAuthPrompt?: boolean;
  // Animation control fields
  displayedContent?: string; // For typewriter effect
  visibleStepsCount?: number; // Number of visible steps
  visibleSectionsCount?: number; // Number of visible sections
  isTyping?: boolean; // Whether content is being typed
}

const chatInput = ref('');
const messagesRef = ref<HTMLDivElement | null>(null);
const composerRef = ref<InstanceType<typeof MockChatComposer> | null>(null);

// composer 内部自处理高度自适应；保留 noop 占位以兼容旧调用点
const autoResizeTextarea = () => {
  /* deprecated: composer 自管 textarea 高度 */
};

// Demo mode state
// initialState='empty' 时：未生成态 —— 仅显示输入框 + pending chip，等待 typeText 启动动画。
// initialState='complete' 时：完成态 —— 直接展示已生成的 deck + chat 历史。
const isArtboardLoading = ref(false);
const showArtboard = ref(props.initialState === 'complete');

// Deck 网格布局参数
const FRAME_GAP = 32;
const DECK_PADDING_X = 24;
const DECK_PADDING_Y = 28;
const LEGACY_ARTBOARD_WIDTH = 320;
const LEGACY_ARTBOARD_HEIGHT = 320;
const hasDeckSlides = computed(
  () => Array.isArray(props.deckSlides) && props.deckSlides.length > 0
);

const artboardWidth = computed(() => props.artboardWidth ?? 360);
const artboardHeight = computed(() => props.artboardHeight ?? 203);
const deckMaxColumns = computed(() => props.deckMaxColumns ?? 3);

/** 自由布局模式：deck slides 中至少一张同时提供 x/y。
 *  此时每个 frame 用 slide 自带的 x/y/width/height 直接定位到画板上，
 *  适合"一个 brief × 多种媒介尺寸（横版 banner / 9:16 邀请函等）"展示。 */
const isFreeLayout = computed(
  () =>
    hasDeckSlides.value &&
    deckSlides.value.some((slide) => typeof slide.x === 'number' && typeof slide.y === 'number')
);

const deckColumnCount = computed(() =>
  Math.max(1, Math.min(deckMaxColumns.value, hasDeckSlides.value ? deckSlides.value.length : 1))
);
const deckRowCount = computed(() =>
  hasDeckSlides.value ? Math.ceil(deckSlides.value.length / deckColumnCount.value) : 1
);

const deckWidth = computed(() => {
  if (!hasDeckSlides.value) return LEGACY_ARTBOARD_WIDTH;
  if (isFreeLayout.value) {
    // 自由布局：优先用 props 显式声明的总宽，缺省时取所有 frame right 的最大值 + padding
    if (props.deckWidth) return props.deckWidth;
    const maxRight = deckSlides.value.reduce((acc, slide) => {
      if (typeof slide.x !== 'number') return acc;
      const w = slide.width ?? artboardWidth.value;
      return Math.max(acc, slide.x + w);
    }, 0);
    return maxRight + DECK_PADDING_X;
  }
  return (
    DECK_PADDING_X * 2 +
    deckColumnCount.value * artboardWidth.value +
    (deckColumnCount.value - 1) * FRAME_GAP
  );
});
const deckHeight = computed(() => {
  if (!hasDeckSlides.value) return LEGACY_ARTBOARD_HEIGHT;
  if (isFreeLayout.value) {
    if (props.deckHeight) return props.deckHeight;
    const maxBottom = deckSlides.value.reduce((acc, slide) => {
      if (typeof slide.y !== 'number') return acc;
      const h = slide.height ?? artboardHeight.value;
      return Math.max(acc, slide.y + h);
    }, 0);
    return maxBottom + DECK_PADDING_Y;
  }
  return (
    DECK_PADDING_Y * 2 +
    deckRowCount.value * artboardHeight.value +
    (deckRowCount.value - 1) * FRAME_GAP
  );
});

// Frame 选中：activeSlideIndex 表示当前激活 frame
const activeSlideIndex = ref(0);

const framePosition = (index: number) => {
  const slide = deckSlides.value[index];
  if (slide && typeof slide.x === 'number' && typeof slide.y === 'number') {
    return { x: slide.x, y: slide.y };
  }
  const column = index % deckColumnCount.value;
  const row = Math.floor(index / deckColumnCount.value);
  return {
    x: DECK_PADDING_X + column * (artboardWidth.value + FRAME_GAP),
    y: DECK_PADDING_Y + row * (artboardHeight.value + FRAME_GAP),
  };
};

const frameSize = (index: number) => {
  const slide = deckSlides.value[index];
  return {
    width: slide?.width ?? artboardWidth.value,
    height: slide?.height ?? artboardHeight.value,
  };
};

const frameStyle = (index: number) => {
  const position = framePosition(index);
  const size = frameSize(index);
  const baseShadow = '0 8px 28px -8px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06)';
  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    left: `${position.x}px`,
    top: `${position.y}px`,
    boxShadow:
      activeSlideIndex.value === index && isArtboardSelected.value
        ? `${baseShadow}, 0 0 0 ${1 / zoom.value}px #FE713B`
        : baseShadow,
  };
};

const MAX_ZOOM = 3;

/** 与 fitDeckToContainer 同一套算法，供 pinch 下限与「缩回全貌」使用 */
const computeFitZoom = (): number => {
  if (!hasDeckSlides.value) return 1;
  if (!containerRef.value) return props.compactChrome ? 0.26 : 0.35;

  const containerWidth = containerRef.value.clientWidth;
  const containerHeight = containerRef.value.clientHeight;
  if (!containerWidth || !containerHeight) return props.compactChrome ? 0.26 : 0.35;

  const padX = props.compactChrome ? 16 : 64;
  const padY = props.compactChrome ? 12 : 80;
  const floor = props.compactChrome ? 0.12 : 0.35;
  return Math.min(
    1,
    Math.max(
      floor,
      Math.min(
        (containerWidth - padX) / deckWidth.value,
        (containerHeight - padY) / deckHeight.value
      )
    )
  );
};

/** 当前容器下的「全貌」缩放；mobile 常远小于 1，不能再用固定 0.5 当下限 */
const fitZoom = ref(1);
const minZoom = computed(() => Math.max(0.12, fitZoom.value * 0.98));

const clampZoom = (value: number) => Math.max(minZoom.value, Math.min(MAX_ZOOM, value));

const applyZoomAtPoint = (nextZoom: number, clientX: number, clientY: number) => {
  const clamped = clampZoom(nextZoom);
  if (!containerRef.value) {
    zoom.value = clamped;
    return;
  }

  const rect = containerRef.value.getBoundingClientRect();
  const focalX = clientX - rect.left - rect.width / 2;
  const focalY = clientY - rect.top - rect.height / 2;
  const scaleChange = clamped / zoom.value;

  const newOffset = {
    x: focalX - (focalX - offset.value.x) * scaleChange,
    y: focalY - (focalY - offset.value.y) * scaleChange,
  };
  zoom.value = clamped;
  offset.value = clampOffset(newOffset, clamped);
};

/** 隐藏/零尺寸容器不参与 refit，避免 scroll-jacking 离屏 MockCanvas 写入错误 zoom */
const containerIsReadyForFit = () => {
  const el = containerRef.value;
  if (!el || el.clientWidth < 48 || el.clientHeight < 48) return false;
  const style = getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  const rect = el.getBoundingClientRect();
  if (rect.width < 48 || rect.height < 48) return false;
  return true;
};

const fitDeckToContainer = () => {
  if (!containerIsReadyForFit()) return;
  const nextZoom = computeFitZoom();
  fitZoom.value = nextZoom;
  zoom.value = nextZoom;
  offset.value = { x: 0, y: 0 };
};

const handleFrameClick = (index: number, e: MouseEvent) => {
  e.stopPropagation();
  activeSlideIndex.value = index;
  selectedElement.value = 'artboard';
};

const handleSlideElementClick = (index: number, elementId: SelectableElement, e: MouseEvent) => {
  e.stopPropagation();
  activeSlideIndex.value = index;
  selectedElement.value = elementId;
};

const handleLegacyArtboardClick = (e: MouseEvent) => {
  e.stopPropagation();
  selectedElement.value = 'artboard';
};

const handleLegacyElementClick = (elementId: SelectableElement, e: MouseEvent) => {
  e.stopPropagation();
  selectedElement.value = elementId;
};

// Demo messages data
const defaultDemoUserMessage = computed(() => {
  void locale.value;
  return t('landing_home.mock.user_message');
});

const defaultDemoAiMessage = computed<ChatMessage>(() => {
  void locale.value;
  return {
    id: 2,
    role: 'ai',
    content: t('landing_home.mock.ai_intro'),
    steps: [
      { label: 'pastel gradient background', done: true },
      { label: 'bold typography hierarchy', done: true },
      { label: '1:1 square for social media', done: true },
      { label: 'celebratory imagery', done: true },
    ],
    sections: [
      { id: 'copy', label: t('landing_home.mock.section.design_concept_label') },
      {
        id: 'concept',
        label: '',
        content: t('landing_home.mock.section.design_concept_content'),
      },
      {
        id: 'theme',
        label: t('landing_home.mock.section.theme_label'),
        value: t('landing_home.mock.section.theme_value'),
        color: '#E8D5F0',
      },
      {
        id: 'imageStyle',
        label: t('landing_home.mock.section.image_style_label'),
        value: t('landing_home.mock.section.image_style_value'),
        icon: '🎉',
      },
      {
        id: 'ratio',
        label: t('landing_home.mock.section.ratio_label'),
        value: t('landing_home.mock.section.ratio_value'),
      },
    ],
  };
});

const demoUserMessage = computed(() => props.demoUserMessage ?? defaultDemoUserMessage.value);
const demoAiMessage = computed(() => props.demoAiMessage ?? defaultDemoAiMessage.value);

const authPromptLinks = computed(() => {
  void locale.value;
  const signIn = t('landing_home.mock.auth.sign_in');
  const signUp = t('landing_home.mock.auth.sign_up');
  const prompt = t('landing_home.mock.auth.prompt');
  const signInIdx = prompt.indexOf(signIn);
  const signUpIdx = prompt.indexOf(signUp);
  if (signInIdx === -1 || signUpIdx === -1) return null;

  const firstIdx = Math.min(signInIdx, signUpIdx);
  const secondIdx = Math.max(signInIdx, signUpIdx);
  const firstLen = signInIdx < signUpIdx ? signIn.length : signUp.length;
  const secondLen = signInIdx < signUpIdx ? signUp.length : signIn.length;

  return {
    before: prompt.slice(0, firstIdx),
    middle: prompt.slice(firstIdx + firstLen, secondIdx),
    after: prompt.slice(secondIdx + secondLen),
    signInFirst: signInIdx < signUpIdx,
    signInLabel: signIn,
    signUpLabel: signUp,
  };
});

const syncDefaultDemoMessages = () => {
  if (props.demoUserMessage || props.demoAiMessage) return;

  const userMsg = messages.value.find((m) => m.role === 'user');
  if (userMsg) {
    userMsg.content = demoUserMessage.value;
  }

  const aiMsg = messages.value.find((m) => m.role === 'ai' && !m.isAuthPrompt);
  if (aiMsg) {
    const updated = demoAiMessage.value;
    aiMsg.content = updated.content;
    aiMsg.steps = updated.steps;
    aiMsg.sections = updated.sections;
    if (aiMsg.displayedContent !== undefined) {
      aiMsg.displayedContent = updated.content;
    }
  }
};

watch(
  () => i18n.locale,
  () => {
    syncDefaultDemoMessages();
  }
);

/** 输入框上方"待发送附件"chip 状态：
 *  - initialState='complete'：null，user message 已在历史里携带 attachment
 *  - initialState='empty'：挂上 demoUserAttachment，让"未生成"态就显示已上传文件
 *  - demo 动画 resetDemo 也会重新挂回去
 */
const pendingAttachment = ref<ChatAttachment | null>(
  props.initialState === 'empty' && props.demoUserAttachment
    ? { ...props.demoUserAttachment }
    : null
);

// Initial messages
// - complete：渲染历史 user + ai 消息
// - empty：空数组，等 typing → sendDemoMessage → showAiMessageAnimated 主动 push
const messages = ref<ChatMessage[]>(
  props.initialState === 'empty'
    ? []
    : [
        {
          id: 1,
          role: 'user',
          content: demoUserMessage.value,
          ...(props.demoUserAttachment ? { attachment: { ...props.demoUserAttachment } } : {}),
        },
        { ...demoAiMessage.value },
      ]
);

let messageIdCounter = 3;

// Demo control methods
const resetDemo = () => {
  messages.value = [];
  chatInput.value = '';
  showArtboard.value = false;
  isArtboardLoading.value = false;
  activeSlideIndex.value = 0;
  selectedElement.value = null;
  isSidebarOpen.value = resolveSidebarOpen(props.defaultSidebarOpen);
  // demo 重新开跑时把附件挂回 input pending 区，模拟"用户拖入文件 + 准备打字"
  pendingAttachment.value = props.demoUserAttachment ? { ...props.demoUserAttachment } : null;
};

/** 仅填充 composer（附件 + 提示词），不推进 chat 气泡 */
const prepareDemoComposer = (content: string) => {
  chatInput.value = content.trim();
  pendingAttachment.value = props.demoUserAttachment ? { ...props.demoUserAttachment } : null;
};

const sleepDemo = async (ms: number) => {
  let remaining = ms;
  while (remaining > 0) {
    while (props.playbackPaused) {
      await new Promise((r) => setTimeout(r, 32));
    }
    const step = Math.min(16, remaining);
    await new Promise((r) => setTimeout(r, step));
    remaining -= step;
  }
};

const typeText = async (text: string, delay = 15) => {
  chatInput.value = '';
  autoResizeTextarea();
  for (const char of text) {
    while (props.playbackPaused) {
      await new Promise((r) => setTimeout(r, 32));
    }
    chatInput.value += char;
    await sleepDemo(delay);
    autoResizeTextarea();
  }
};

/**
 * 把一条用户消息推进 chat 区。
 * - 无参（原行为）：从 chatInput.value 取内容，配合 typeText 走"输入框 typing → 发送"。
 * - 传 overrideContent：跳过 chatInput / typing 直接 push，适用于 OneInputManyOutputs 的
 *   workspace 模式（typing 阶段在外层独立 composer 已经演示过，进入 canvas 时直接出消息）。
 */
const sendDemoMessage = (overrideContent?: string) => {
  const content = (overrideContent ?? chatInput.value).trim();
  if (!content) return;
  // 附件优先取 pending 区（demo 模式下由 resetDemo 注入），
  // 否则回退到 prop 原始值——避免 pendingAttachment 因任何时序原因为 null 时丢掉附件。
  const attachment = pendingAttachment.value ?? props.demoUserAttachment ?? null;
  messages.value.push({
    id: messageIdCounter++,
    role: 'user',
    content,
    ...(attachment ? { attachment: { ...attachment } } : {}),
  });
  chatInput.value = '';
  pendingAttachment.value = null;
  scrollToBottom();
};

const showAiMessage = () => {
  messages.value.push({ ...demoAiMessage.value, id: messageIdCounter++ });
  scrollToBottom();
};

// Animated AI message display with typewriter effect
const showAiMessageAnimated = async () => {
  const messageId = messageIdCounter++;
  const aiMessage = demoAiMessage.value;
  const newMessage: ChatMessage = {
    ...aiMessage,
    id: messageId,
    displayedContent: '',
    visibleStepsCount: 0,
    visibleSectionsCount: 0,
    isTyping: true,
  };

  messages.value.push(newMessage);
  scrollToBottom();

  // Find the message in the array
  const messageIndex = messages.value.findIndex((m) => m.id === messageId);
  if (messageIndex === -1) return;
  const message = messages.value[messageIndex];
  if (!message) return;

  // Step 1: Typewriter effect for content
  const fullContent = aiMessage.content;
  for (let i = 0; i <= fullContent.length; i++) {
    while (props.playbackPaused) {
      await new Promise((r) => setTimeout(r, 32));
    }
    message.displayedContent = fullContent.slice(0, i);
    await sleepDemo(8);
  }
  message.isTyping = false;
  await sleepDemo(100);
  scrollToBottom();

  // Step 2: Show steps one by one
  const stepsCount = aiMessage.steps?.length || 0;
  for (let i = 1; i <= stepsCount; i++) {
    while (props.playbackPaused) {
      await new Promise((r) => setTimeout(r, 32));
    }
    message.visibleStepsCount = i;
    scrollToBottom();
    await sleepDemo(80);
  }
  await sleepDemo(150);

  // Step 3: Show sections one by one
  const sectionsCount = aiMessage.sections?.length || 0;
  for (let i = 1; i <= sectionsCount; i++) {
    while (props.playbackPaused) {
      await new Promise((r) => setTimeout(r, 32));
    }
    message.visibleSectionsCount = i;
    scrollToBottom();
    await sleepDemo(100);
  }
};

const startArtboardLoading = () => {
  isArtboardLoading.value = true;
  showArtboard.value = true;
};

const finishArtboardLoading = () => {
  isArtboardLoading.value = false;
  void nextTick(fitDeckToContainer);
};

const scrollToBottom = () => {
  setTimeout(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
    }
  }, 50);
};

onMounted(() => {
  void nextTick(() => {
    fitZoom.value = computeFitZoom();
    fitDeckToContainer();
  });
  window.addEventListener('resize', fitDeckToContainer);
});

onUnmounted(() => {
  window.removeEventListener('resize', fitDeckToContainer);
});

// Expose methods for parent control
defineExpose({
  resetDemo,
  prepareDemoComposer,
  typeText,
  sendDemoMessage,
  showAiMessage,
  showAiMessageAnimated,
  startArtboardLoading,
  finishArtboardLoading,
  fitDeckToContainer,
  setSidebarOpen,
  messages,
  chatInput,
  showArtboard,
  isArtboardLoading,
});

const handleSendMessage = () => {
  if (chatInput.value.trim()) {
    // Add user message
    messages.value.push({
      id: messageIdCounter++,
      role: 'user',
      content: chatInput.value.trim(),
    });
    chatInput.value = '';

    // Scroll to bottom
    setTimeout(() => {
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
      }
    }, 50);

    // Add AI response after a short delay
    setTimeout(() => {
      messages.value.push({
        id: messageIdCounter++,
        role: 'ai',
        content: t('landing_home.mock.auth.prompt'),
        isAuthPrompt: true,
      });
      // Scroll to bottom again
      setTimeout(() => {
        if (messagesRef.value) {
          messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
        }
      }, 50);
    }, 500);
  }
};

// Touch state
const touchState = ref<{
  touches: { x: number; y: number }[];
  initialDistance: number;
  initialZoom: number;
  initialOffset: { x: number; y: number };
  lastTouchCenter: { x: number; y: number };
  lastPinchDistance: number;
} | null>(null);

// rAF 合并：高频拖拽/滚轮/触屏事件只缓存最新输入，每帧统一提交一次 transform，
// 避免 mousemove 频率高于刷新率时的 layout thrash 与 Vue 多次重渲染。
const pendingDragMouse = { active: false, x: 0, y: 0 };
const pendingWheel = { deltaY: 0, clientX: 0, clientY: 0 };
const pendingTouchPan = { active: false, dx: 0, dy: 0 };
let transformRafId: number | null = null;

const flushPendingTransform = () => {
  // 1. wheel zoom（触控板 pinch）
  if (pendingWheel.deltaY !== 0) {
    const delta = -pendingWheel.deltaY * 0.008;
    applyZoomAtPoint(zoom.value + delta, pendingWheel.clientX, pendingWheel.clientY);
    pendingWheel.deltaY = 0;
  }

  // 2. mouse drag
  if (pendingDragMouse.active) {
    offset.value = clampOffset({
      x: pendingDragMouse.x - dragStart.value.x,
      y: pendingDragMouse.y - dragStart.value.y,
    });
    pendingDragMouse.active = false;
  }

  // 3. touch single-finger pan (累积 dx/dy)
  if (pendingTouchPan.active) {
    offset.value = clampOffset({
      x: offset.value.x + pendingTouchPan.dx,
      y: offset.value.y + pendingTouchPan.dy,
    });
    pendingTouchPan.active = false;
    pendingTouchPan.dx = 0;
    pendingTouchPan.dy = 0;
  }
};

const scheduleTransformFrame = () => {
  if (transformRafId !== null) return;
  transformRafId = requestAnimationFrame(() => {
    transformRafId = null;
    flushPendingTransform();
  });
};

// Handle wheel zoom (touchpad pinch)
const handleWheel = (e: WheelEvent) => {
  if (e.ctrlKey || e.metaKey) {
    // preventDefault 必须同步调用
    e.preventDefault();
    pendingWheel.deltaY += e.deltaY;
    pendingWheel.clientX = e.clientX;
    pendingWheel.clientY = e.clientY;
    scheduleTransformFrame();
  }
};

// Handle mouse drag
const handleMouseDown = (e: MouseEvent) => {
  isDragging.value = true;
  dragStart.value = { x: e.clientX - offset.value.x, y: e.clientY - offset.value.y };
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return;
  pendingDragMouse.active = true;
  pendingDragMouse.x = e.clientX;
  pendingDragMouse.y = e.clientY;
  scheduleTransformFrame();
};

const handleMouseUp = () => {
  isDragging.value = false;
};

onBeforeUnmount(() => {
  if (transformRafId !== null) {
    cancelAnimationFrame(transformRafId);
    transformRafId = null;
  }
});

// Touch helpers
const getTouchDistance = (t1: { x: number; y: number }, t2: { x: number; y: number }) => {
  const dx = t2.x - t1.x;
  const dy = t2.y - t1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchCenter = (t1: { x: number; y: number }, t2: { x: number; y: number }) => {
  return { x: (t1.x + t2.x) / 2, y: (t1.y + t2.y) / 2 };
};

// Handle touch events — landing mobile demo 不拦截单指，交给页面纵向滚动
const handleTouchStart = (e: TouchEvent) => {
  if (props.allowPageScroll && e.touches.length === 1) return;
  e.preventDefault();

  const touches = Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY }));
  const firstTouch = touches[0];
  const secondTouch = touches[1];

  if (touches.length === 1 && firstTouch) {
    touchState.value = {
      touches,
      initialDistance: 0,
      initialZoom: zoom.value,
      initialOffset: { ...offset.value },
      lastTouchCenter: firstTouch,
      lastPinchDistance: 0,
    };
  } else if (touches.length === 2 && firstTouch && secondTouch) {
    const distance = getTouchDistance(firstTouch, secondTouch);
    const center = getTouchCenter(firstTouch, secondTouch);
    touchState.value = {
      touches,
      initialDistance: distance,
      initialZoom: zoom.value,
      initialOffset: { ...offset.value },
      lastTouchCenter: center,
      lastPinchDistance: distance,
    };
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (props.allowPageScroll && (!touchState.value || e.touches.length === 1)) return;
  e.preventDefault();
  if (!touchState.value) return;

  const touches = Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY }));
  const firstTouch = touches[0];
  const secondTouch = touches[1];

  if (touches.length === 1 && touchState.value.touches.length === 1 && firstTouch) {
    // Pan: 立即累积 delta 到 pending，rAF 内统一提交
    const deltaX = firstTouch.x - touchState.value.lastTouchCenter.x;
    const deltaY = firstTouch.y - touchState.value.lastTouchCenter.y;
    pendingTouchPan.active = true;
    pendingTouchPan.dx += deltaX;
    pendingTouchPan.dy += deltaY;
    touchState.value.lastTouchCenter = firstTouch;
    scheduleTransformFrame();
  } else if (
    touches.length === 2 &&
    touchState.value.initialDistance > 0 &&
    firstTouch &&
    secondTouch
  ) {
    const currentDistance = getTouchDistance(firstTouch, secondTouch);
    const currentCenter = getTouchCenter(firstTouch, secondTouch);
    const lastDistance = touchState.value.lastPinchDistance || touchState.value.initialDistance;
    if (lastDistance > 0) {
      const ratio = currentDistance / lastDistance;
      applyZoomAtPoint(zoom.value * ratio, currentCenter.x, currentCenter.y);
    }
    touchState.value.lastPinchDistance = currentDistance;
    touchState.value.lastTouchCenter = currentCenter;
  }
};

const handleTouchEnd = (e: TouchEvent) => {
  if (e.touches.length === 0) {
    const wasPinching = (touchState.value?.lastPinchDistance ?? 0) > 0;
    touchState.value = null;
    if (wasPinching && Math.abs(zoom.value - fitZoom.value) <= fitZoom.value * 0.1) {
      fitDeckToContainer();
    }
  } else if (e.touches.length === 1) {
    const touch = e.touches[0];
    if (touchState.value && touch) {
      touchState.value.touches = [{ x: touch.clientX, y: touch.clientY }];
      touchState.value.initialOffset = { ...offset.value };
      touchState.value.lastTouchCenter = { x: touch.clientX, y: touch.clientY };
      touchState.value.lastPinchDistance = 0;
      touchState.value.initialDistance = 0;
    }
  }
};

// Zoom controls
const _zoomIn = () => {
  if (!containerRef.value) {
    zoom.value = clampZoom(zoom.value + 0.25);
    return;
  }
  const rect = containerRef.value.getBoundingClientRect();
  applyZoomAtPoint(zoom.value + 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
};

const _zoomOut = () => {
  if (!containerRef.value) {
    zoom.value = clampZoom(zoom.value - 0.25);
    return;
  }
  const rect = containerRef.value.getBoundingClientRect();
  applyZoomAtPoint(zoom.value - 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
};

const _resetZoom = () => {
  fitDeckToContainer();
};

const _zoomPercent = computed(() => Math.round(zoom.value * 100));

// Clamp offset to prevent the flat deck from sliding out of container
const clampOffset = (newOffset: { x: number; y: number }, zoomOverride?: number) => {
  if (!containerRef.value) return newOffset;

  const container = containerRef.value;
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;

  const z = zoomOverride ?? zoom.value;
  const artboardWidth = deckWidth.value * z;
  const artboardHeight = deckHeight.value * z;

  // 至少保留一定比例的画板在容器内可见
  const minVisible = Math.min(100, artboardWidth * 0.3, artboardHeight * 0.3);

  const maxX = containerWidth / 2 + artboardWidth / 2 - minVisible;
  const minX = -maxX;

  const maxY = containerHeight / 2 + artboardHeight / 2 - minVisible;
  const minY = -maxY;

  return {
    x: Math.max(minX, Math.min(maxX, newOffset.x)),
    y: Math.max(minY, Math.min(maxY, newOffset.y)),
  };
};
</script>

<template>
  <div
    class="relative w-full h-full rounded-xl overflow-hidden flex flex-col md:flex-row bg-[#fafafa] border border-black/[0.03]"
  >
    <!-- Left: Canvas Area -->
    <div class="relative isolate z-0 min-h-0 min-w-0 flex-1">
      <!-- Top Bar -->
      <div
        v-if="!compactChrome"
        class="pointer-events-none absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-2 py-1"
      >
        <!-- Left: Logo + Title -->
        <div
          class="pointer-events-auto flex items-center gap-1 px-2 py-1 rounded-lg bg-[#fafafa]/80 backdrop-blur-[20px]"
        >
          <ImageLazy
            src="/veesoai-condense-dark.svg"
            alt="Veeso AI"
            class="h-5 w-auto"
            image-class="h-5 w-auto opacity-40"
          />
          <span class="whitespace-nowrap text-black/30 text-xs font-normal">{{
            props.title || $t('landing_home.mock.canvas.untitled_design')
          }}</span>
        </div>

        <!-- Right: Actions -->
        <div class="pointer-events-auto flex items-center gap-1">
          <!-- Share Button -->
          <div class="relative">
            <button
              @click="toggleSharePanel"
              class="px-2 py-1 flex items-center gap-1 rounded-lg bg-[#fafafa]/80 backdrop-blur-[20px] text-black/50 hover:text-black hover:bg-[#fafafa] transition-colors text-sm font-medium"
            >
              {{ $t('landing_home.mock.canvas.share_button') }}
            </button>

            <!-- Share Panel -->
            <Transition name="fade">
              <div
                v-if="isSharePanelOpen"
                class="absolute top-full right-0 mt-2 w-80 p-2 rounded-2xl bg-white border border-black/5 shadow-xl z-20"
              >
                <div class="space-y-3">
                  <p class="text-black/70 text-sm px-1">{{ shareText }}</p>
                  <div class="flex items-center justify-between">
                    <!-- Left: Copy link button -->
                    <button
                      @click="copyShareLink"
                      class="px-3 py-1.5 text-xs font-medium text-black/70 hover:text-black bg-black/5 hover:bg-black/10 rounded-full transition-colors flex items-center gap-1.5"
                    >
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                      {{ $t('landing_home.mock.canvas.copy_link') }}
                    </button>

                    <!-- Right: Share to -->
                    <div class="flex items-center gap-2">
                      <span class="text-black/40 text-xs">{{
                        $t('landing_home.mock.canvas.share_to')
                      }}</span>
                      <a
                        :href="`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`"
                        target="_blank"
                        class="w-7 h-7 flex items-center justify-center rounded-full bg-black/5 hover:bg-black/10 transition-colors"
                      >
                        <svg class="w-3 h-3 text-black/70" viewBox="0 0 24 24" fill="currentColor">
                          <path
                            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                          />
                        </svg>
                      </a>
                      <a
                        :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`"
                        target="_blank"
                        class="w-7 h-7 flex items-center justify-center rounded-full bg-black/5 hover:bg-black/10 transition-colors"
                      >
                        <svg class="w-3 h-3 text-black/70" viewBox="0 0 24 24" fill="currentColor">
                          <path
                            d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Toggle Sidebar Button -->
          <button
            v-if="!hideChatHistory"
            type="button"
            @click.stop="toggleSidebar"
            class="relative z-40 w-7 h-7 flex items-center justify-center rounded-lg bg-[#fafafa]/80 backdrop-blur-[20px] text-black/50 hover:text-black hover:bg-[#fafafa] transition-colors"
            :title="
              isSidebarOpen
                ? $t('landing_home.mock.canvas.collapse_sidebar')
                : $t('landing_home.mock.canvas.expand_sidebar')
            "
          >
            <svg class="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2.5" y="2.5" width="11" height="11" stroke="currentColor" />
              <path d="M10 4V12" stroke="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Canvas Container -->
      <div
        ref="containerRef"
        class="absolute inset-0 z-0 flex items-center justify-center overflow-hidden"
        :class="{
          'cursor-default': activeTool === 'select' || activeTool === 'deep-select',
          'cursor-grab active:cursor-grabbing': activeTool === 'hand',
        }"
        :style="{
          touchAction: allowPageScroll ? 'pan-y' : 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
        @wheel="handleWheel"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @click="handleCanvasClick"
      >
        <!-- Render legacy artboard unless deck slides are explicitly provided -->
        <Transition
          enter-active-class="transition-all duration-500 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <template v-if="showArtboard">
            <div
              v-if="hasDeckSlides"
              v-bind="DECK_COPY_ATTRS"
              class="deck-canvas notranslate relative transition-all duration-500"
              :style="{
                width: `${deckWidth}px`,
                height: `${deckHeight}px`,
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                background: 'transparent',
                flexShrink: 0,
              }"
            >
              <div
                v-for="(slide, index) in deckSlides"
                :key="slide.id"
                class="wgp-frame absolute bg-white"
                :style="frameStyle(index)"
                @click="handleFrameClick(index, $event)"
              >
                <div
                  class="wgp-frame-label absolute left-0 whitespace-nowrap text-[11px] font-medium"
                  :class="
                    activeSlideIndex === index && isArtboardSelected
                      ? 'text-[#FE713B]'
                      : 'text-black/40'
                  "
                  :style="{
                    top: `${-18 / zoom}px`,
                    transform: `scale(${1 / zoom})`,
                    transformOrigin: 'bottom left',
                  }"
                >
                  {{ slide.label }}
                </div>

                <Transition
                  enter-active-class="transition-opacity duration-300"
                  enter-from-class="opacity-0"
                  enter-to-class="opacity-100"
                  leave-active-class="transition-opacity duration-500"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <SeedeFrameLoading
                    v-if="isArtboardLoading"
                    class="absolute inset-0 z-20"
                    :class="{ 'is-playback-paused': playbackPaused }"
                    :quality="activeSlideIndex === index ? 'high' : 'balanced'"
                  />
                </Transition>
                <template v-if="activeSlideIndex === index && isArtboardSelected">
                  <div
                    class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nwse-resize z-30"
                    :style="{
                      top: `${-4 / zoom}px`,
                      left: `${-4 / zoom}px`,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: 'center center',
                    }"
                  />
                  <div
                    class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nesw-resize z-30"
                    :style="{
                      top: `${-4 / zoom}px`,
                      right: `${-4 / zoom}px`,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: 'center center',
                    }"
                  />
                  <div
                    class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nesw-resize z-30"
                    :style="{
                      bottom: `${-4 / zoom}px`,
                      left: `${-4 / zoom}px`,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: 'center center',
                    }"
                  />
                  <div
                    class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nwse-resize z-30"
                    :style="{
                      bottom: `${-4 / zoom}px`,
                      right: `${-4 / zoom}px`,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: 'center center',
                    }"
                  />
                </template>

                <!-- Slide content：固定英文设计稿，禁止浏览器整页翻译改写 -->
                <div
                  v-if="slide.component"
                  v-bind="DECK_COPY_ATTRS"
                  class="deck-slide-shell notranslate"
                >
                  <component
                    :is="slide.component"
                    :selected-element="activeSlideIndex === index ? selectedElement : null"
                    @select="
                      (elementId: string, event: MouseEvent) =>
                        handleSlideElementClick(index, elementId, event)
                    "
                  />
                </div>
              </div>
            </div>

            <div
              v-else
              class="artboard relative transition-all duration-500"
              :style="{
                width: '320px',
                height: '320px',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                background: isArtboardLoading
                  ? '#ffffff'
                  : 'linear-gradient(180deg, #E8D5F0 0%, #C5E8E8 50%, #F5D5C8 100%)',
                flexShrink: 0,
                boxShadow: isArtboardSelected ? `0 0 0 ${1 / zoom}px #FE713B` : 'none',
              }"
              @click="handleLegacyArtboardClick"
            >
              <Transition
                enter-active-class="transition-opacity duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-500"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <div
                  v-if="isArtboardLoading"
                  class="absolute inset-0 z-10 flex items-center justify-center bg-white"
                >
                  <div class="flex flex-col items-center gap-3">
                    <div
                      class="w-8 h-8 border-2 border-black/10 border-t-black/40 rounded-full animate-spin"
                    />
                    <span class="text-black/40 text-xs font-medium">{{
                      $t('landing_home.mock.canvas.generating')
                    }}</span>
                  </div>
                </div>
              </Transition>
              <template v-if="isArtboardSelected">
                <div
                  class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nwse-resize"
                  :style="{
                    top: `${-4 / zoom}px`,
                    left: `${-4 / zoom}px`,
                    transform: `scale(${1 / zoom})`,
                    transformOrigin: 'center center',
                  }"
                />
                <div
                  class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nesw-resize"
                  :style="{
                    top: `${-4 / zoom}px`,
                    right: `${-4 / zoom}px`,
                    transform: `scale(${1 / zoom})`,
                    transformOrigin: 'center center',
                  }"
                />
                <div
                  class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nesw-resize"
                  :style="{
                    bottom: `${-4 / zoom}px`,
                    left: `${-4 / zoom}px`,
                    transform: `scale(${1 / zoom})`,
                    transformOrigin: 'center center',
                  }"
                />
                <div
                  class="absolute w-2 h-2 bg-white border border-[#FE713B] cursor-nwse-resize"
                  :style="{
                    bottom: `${-4 / zoom}px`,
                    right: `${-4 / zoom}px`,
                    transform: `scale(${1 / zoom})`,
                    transformOrigin: 'center center',
                  }"
                />
              </template>

              <div class="absolute inset-0 flex flex-col p-2">
                <div class="flex justify-between items-start">
                  <div
                    class="flex-1 relative cursor-pointer"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'title' }"
                    @click="handleLegacyElementClick('title', $event)"
                  >
                    <div class="font-bold text-black leading-[1] tracking-tight text-[28px]">
                      {{ $t('landing_home.mock.poster.title_line1') }} <br />
                      {{ $t('landing_home.mock.poster.title_line2') }} <br />
                      {{ $t('landing_home.mock.poster.title_line3') }}
                    </div>
                  </div>
                  <div
                    class="text-left relative cursor-pointer"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'name' }"
                    @click="handleLegacyElementClick('name', $event)"
                  >
                    <p class="font-normal py-2 px-2 text-black text-xs leading-[1]">
                      <template
                        v-for="(line, lineIndex) in $t('landing_home.mock.poster.name').split('\n')"
                        :key="lineIndex"
                      >
                        <br v-if="lineIndex > 0" />{{ line }}
                      </template>
                    </p>
                  </div>
                </div>

                <div class="grid grid-cols-4 mt-6 gap-2">
                  <div
                    v-for="featureKey in ['feature1', 'feature2', 'feature3', 'feature4'] as const"
                    :key="featureKey"
                    class="relative cursor-pointer"
                    :class="{
                      'ring-1 ring-[#FE713B]': selectedElement === featureKey,
                    }"
                    @click="handleLegacyElementClick(featureKey, $event)"
                  >
                    <p class="text-black/70 leading-[1.1] text-[8px]">
                      <template
                        v-for="(line, lineIndex) in $t(
                          `landing_home.mock.poster.${featureKey}`
                        ).split('\n')"
                        :key="lineIndex"
                      >
                        <br v-if="lineIndex > 0" />{{ line }}
                      </template>
                    </p>
                  </div>
                </div>

                <div class="flex-1 flex items-center justify-end mt-2 relative z-10">
                  <!-- 日文分支：字号 -8px、整体右移 10px。i18n 长文本节奏不同，与英文样式解耦 -->
                  <div
                    v-if="isJapanese"
                    class="text-left relative cursor-pointer ml-[10px]"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'number' }"
                    @click="handleLegacyElementClick('number', $event)"
                  >
                    <p class="font-black text-[#E53935] leading-[0.9] tracking-tight text-[24px]">
                      <template
                        v-for="(line, lineIndex) in $t('landing_home.mock.poster.number').split(
                          '\n'
                        )"
                        :key="lineIndex"
                      >
                        <br v-if="lineIndex > 0" />{{ line }}
                      </template>
                    </p>
                  </div>
                  <!-- 英文 / 默认分支：保持改动前原样 -->
                  <div
                    v-else
                    class="text-left relative cursor-pointer"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'number' }"
                    @click="handleLegacyElementClick('number', $event)"
                  >
                    <p class="font-black text-[#E53935] leading-[0.9] tracking-tight text-[32px]">
                      <template
                        v-for="(line, lineIndex) in $t('landing_home.mock.poster.number').split(
                          '\n'
                        )"
                        :key="lineIndex"
                      >
                        <br v-if="lineIndex > 0" />{{ line }}
                      </template>
                    </p>
                  </div>
                </div>

                <div
                  class="absolute w-52 h-auto -left-4 bottom-0 cursor-pointer z-0"
                  :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'image' }"
                  @click="handleLegacyElementClick('image', $event)"
                >
                  <ImageLazy
                    src="/left.webp"
                    :alt="$t('landing_home.mock.poster.image_alt')"
                    class="w-full h-auto"
                    image-class="object-contain w-full h-auto"
                    :draggable="false"
                  />
                </div>

                <div class="flex justify-between items-end mt-2">
                  <div
                    class="relative cursor-pointer"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'logo' }"
                    @click="handleLegacyElementClick('logo', $event)"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 63 63"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M42.9849 26.2959C44.8605 26.8182 46.4256 27.9315 47.4644 29.7656C51.6549 37.1654 46.7943 45.5647 36.5483 49.6455C25.8051 53.924 14.4439 49.1764 14.1958 49.0488C14.0444 48.9707 13.9573 48.7802 13.9771 48.6318C14.0035 48.4466 17.3455 41.7327 22.6401 35.2021C22.7352 35.0848 22.9226 35.1234 22.979 35.2627C23.0562 35.4526 23.1447 35.6431 23.2466 35.832C25.1668 39.3823 31.6289 38.1866 35.5464 34.6719C38.7339 32.1848 42.0932 27.2018 42.7056 26.3916V26.3906C42.7709 26.3057 42.8811 26.2672 42.9849 26.2959ZM26.3081 13.2119C37.0536 8.93257 48.4173 13.682 48.6606 13.8086C48.904 13.9352 48.9165 14.1036 48.8403 14.3252C48.8137 14.3729 44.0359 22.9316 40.5269 27.2861C40.4962 27.3247 40.4521 27.3774 40.4126 27.4258C40.3552 27.495 40.2594 27.519 40.1763 27.4814C40.0071 27.4062 39.7182 27.2775 39.4858 27.1846C37.5726 26.4179 33.594 25.228 29.5669 26.4287C25.497 27.6426 22.7481 30.3544 22.6284 33.2451C22.6235 33.3579 22.5786 33.4677 22.5024 33.5469C22.4264 33.6258 22.3519 33.7062 22.2778 33.7861C21.439 34.6952 20.6668 35.5866 19.9595 36.4512C19.9051 36.5175 19.816 36.5462 19.7329 36.5215C17.9186 35.9833 16.4051 34.8804 15.3921 33.0918C11.2015 25.692 16.0621 17.2927 26.3081 13.2119Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                  <div
                    class="relative cursor-pointer"
                    :class="{ 'ring-1 ring-[#FE713B]': selectedElement === 'brand' }"
                    @click="handleLegacyElementClick('brand', $event)"
                  >
                    <p class="font-medium text-black text-xs">
                      {{ $t('landing_home.mock.poster.brand') }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="isArtboardSelected"
                class="absolute left-0 text-[#FE713B] font-medium whitespace-nowrap text-[11px]"
                :style="{
                  top: `${-20 / zoom}px`,
                  transform: `scale(${1 / zoom})`,
                  transformOrigin: 'bottom left',
                }"
              >
                {{ $t('landing_home.mock.canvas.frame_label') }}
              </div>
            </div>
          </template>
        </Transition>
      </div>
    </div>

    <!-- Right: Chat Panel -->
    <div
      v-show="isSidebarOpen"
      class="w-full md:w-[320px] h-[50%] md:h-auto flex flex-col border-t md:border-t-0 md:border-l border-black/5 bg-white relative"
    >
      <!-- Messages -->
      <div
        v-if="!hideChatHistory"
        ref="messagesRef"
        class="mx-auto w-[90%] flex-1 overflow-y-auto pt-4 pb-20 md:pb-24 space-y-6"
      >
        <!-- Messages List -->
        <div v-for="message in messages" :key="message.id">
          <!-- User Message -->
          <div v-if="message.role === 'user'" class="flex justify-end mb-2">
            <div class="max-w-[85%] w-fit space-y-1.5">
              <!-- Attachment chip（用户上传的文件，作为消息的一部分） -->
              <div
                v-if="message.attachment"
                class="ml-auto flex w-fit items-center gap-2 rounded-2xl border border-black/5 bg-white px-2.5 py-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
              >
                <span
                  class="flex h-7 w-7 items-center justify-center rounded-lg"
                  :class="{
                    'bg-[#1a73e8]/10 text-[#1a73e8]':
                      message.attachment.type === 'doc' || !message.attachment.type,
                    'bg-[#e53935]/10 text-[#e53935]': message.attachment.type === 'pdf',
                    'bg-[#000]/[0.04] text-black/70': message.attachment.type === 'md',
                    'bg-[#13a17a]/10 text-[#13a17a]': message.attachment.type === 'image',
                  }"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9 13h6" />
                    <path d="M9 17h6" />
                  </svg>
                </span>
                <div class="min-w-0 leading-tight">
                  <p class="truncate text-[12px] font-medium text-black/80">
                    {{ message.attachment.name }}
                  </p>
                  <p v-if="message.attachment.size" class="text-[10px] text-black/40">
                    {{ message.attachment.size }}
                  </p>
                </div>
              </div>
              <!-- 文字消息气泡 -->
              <div
                class="ml-auto w-fit max-w-full rounded-2xl bg-black/[0.03] px-3 py-1.5 text-[13px]"
              >
                <p class="text-black/70">{{ message.content }}</p>
              </div>
            </div>
          </div>

          <!-- AI Response -->
          <div v-else class="space-y-3 pb-4">
            <!-- Auth Prompt with links -->
            <p v-if="message.isAuthPrompt" class="text-black/70 text-sm">
              <template v-if="authPromptLinks">
                {{ authPromptLinks.before }}
                <a
                  :href="
                    authPromptLinks.signInFirst
                      ? 'https://veeso.ai/login'
                      : 'https://veeso.ai/signup'
                  "
                  target="_blank"
                  class="text-black font-medium hover:underline"
                  >{{
                    authPromptLinks.signInFirst
                      ? authPromptLinks.signInLabel
                      : authPromptLinks.signUpLabel
                  }}</a
                >
                {{ authPromptLinks.middle }}
                <a
                  :href="
                    authPromptLinks.signInFirst
                      ? 'https://veeso.ai/signup'
                      : 'https://veeso.ai/login'
                  "
                  target="_blank"
                  class="text-black font-medium hover:underline"
                  >{{
                    authPromptLinks.signInFirst
                      ? authPromptLinks.signUpLabel
                      : authPromptLinks.signInLabel
                  }}</a
                >
                {{ authPromptLinks.after }}
              </template>
              <template v-else>
                {{ $t('landing_home.mock.auth.prompt') }}
              </template>
            </p>
            <!-- Regular AI message with typewriter effect -->
            <p v-else class="text-black/70 text-sm">
              {{
                message.displayedContent !== undefined ? message.displayedContent : message.content
              }}
              <span
                v-if="message.isTyping"
                class="inline-block w-0.5 h-4 bg-black/40 animate-pulse ml-0.5 align-middle"
              />
            </p>

            <!-- Sections (if any) - with animation -->
            <template v-if="message.sections">
              <TransitionGroup
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
              >
                <div
                  v-for="section in message.sections.slice(
                    0,
                    message.visibleSectionsCount ?? message.sections.length
                  )"
                  :key="section.id"
                  class="space-y-2"
                >
                  <!-- Section Header (hide for sections with content, as title is inside card) -->
                  <div v-if="!section.content" class="flex items-center">
                    <span class="text-black/70 text-sm">{{ section.label }}</span>
                  </div>

                  <!-- Section Content -->
                  <div
                    v-if="section.content"
                    class="px-3 py-1.5 bg-black/[0.03] border border-white/[0.03] rounded-xl space-y-1.5"
                  >
                    <p v-if="section.label" class="text-black/70 text-sm font-medium">
                      {{ section.label }}
                    </p>
                    <p class="text-black/70 text-sm">{{ section.content }}</p>
                  </div>

                  <!-- Theme with color -->
                  <div
                    v-else-if="section.color"
                    class="px-3 py-2 bg-black/[0.03] border border-white/[0.03] rounded-xl flex items-center gap-2"
                  >
                    <div class="w-5 h-5 rounded" :style="{ backgroundColor: section.color }" />
                    <span class="text-black/70 text-sm">{{ section.value }}</span>
                  </div>

                  <!-- Image style with icon -->
                  <div
                    v-else-if="section.icon"
                    class="px-3 py-1 bg-black/[0.03] border border-white/[0.03] rounded-xl flex items-center gap-2"
                  >
                    <span class="text-lg">{{ section.icon }}</span>
                    <span class="text-black/70 text-sm">{{ section.value }}</span>
                  </div>

                  <!-- Ratio -->
                  <div
                    v-else-if="section.value"
                    class="px-3 py-2 bg-black/[0.03] border border-white/[0.03] rounded-xl flex items-center gap-2"
                  >
                    <div class="w-5 h-5 border border-black/30 rounded-sm" />
                    <span class="text-black/70 text-sm">{{ section.value }}</span>
                  </div>
                </div>
              </TransitionGroup>
            </template>
          </div>
        </div>
      </div>

      <!-- Input (Fixed at bottom) -->
      <div
        class="absolute bottom-0 left-0 right-0 pb-2 md:pb-3 bg-gradient-to-t from-[#ffffff] to-[#ffffff]/0 [&>*]:mx-auto [&>*]:w-[90%]"
      >
        <MockChatComposer
          ref="composerRef"
          v-model="chatInput"
          :placeholder="$t('landing_home.mock.canvas.composer_placeholder')"
          :attachment="pendingAttachment"
          :auto-resize="props.composerAutoResize"
          @send="handleSendMessage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sidebar-enter-active,
.sidebar-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

/* 移动端：从底部往上出现 */
.sidebar-enter-from,
.sidebar-leave-to {
  height: 0 !important;
  min-height: 0 !important;
  opacity: 0;
  padding: 0;
  border-top-width: 0;
}

/* 桌面端：从右侧出现 */
@media (min-width: 768px) {
  .sidebar-enter-from,
  .sidebar-leave-to {
    height: auto !important;
    min-height: auto !important;
    width: 0 !important;
    min-width: 0 !important;
    opacity: 0;
    padding: 0;
    border-top-width: 1px;
    border-left-width: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.seede-frame-loading.is-playback-paused .seede-frame-loading__logo {
  animation-play-state: paused;
}

/* attachment chip 动画已移到 MockChatComposer 内部 */

/* Custom scrollbar for messages panel */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.01);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.01);
}

/* === Deck Frame 容器 ===
 * 所有 slide 的 stage 都 transform scale 到 frame 尺寸（layout box 仍是原稿大小），
 * 单层 overflow: hidden 在某些浏览器下不能完全裁掉缩放后的 layout box，
 * 因此叠加 clip-path + contain + isolation 兜底，保证 slide 内容不会溢出 frame */
.wgp-frame {
  overflow: hidden;
  clip-path: inset(0);
  contain: paint;
  isolation: isolate;
}
</style>
